import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MousePointerClick } from 'lucide-react';
import { Hero3DLogo } from './Hero3DLogo';
import { useRadio } from '../context/RadioContext';

export const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const parallaxRef = useRef(null);
  const navigate = useNavigate();
  const { isRadioActive } = useRadio();

  const dismissHint = useCallback(() => {
    setShowHint(false);
  }, []);

  const goToReleases = useCallback(() => {
    dismissHint();
    navigate('/releases');
  }, [navigate, dismissHint]);

  useEffect(() => {
    // Trigger fade-in animation after component mounts
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Subtle parallax: tagline drifts opposite the emblem's mouse tilt for depth
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }
    const onMove = (e) => {
      const el = parallaxRef.current;
      if (!el) return;
      const dx = (e.clientX / window.innerWidth - 0.5) * -18;
      const dy = (e.clientY / window.innerHeight - 0.5) * -10;
      el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
    };
    const onLeave = () => {
      if (parallaxRef.current) parallaxRef.current.style.transform = 'translate3d(0,0,0)';
    };
    window.addEventListener('mousemove', onMove);
    document.addEventListener('mouseleave', onLeave);
    return () => {
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  useEffect(() => {
    // Show the hint shortly after load; auto-hide after 8s or on scroll/interaction
    const showTimer = setTimeout(() => setShowHint(true), 1200);
    const hideTimer = setTimeout(() => setShowHint(false), 9200);
    const onScroll = () => {
      if (window.scrollY > 40) setShowHint(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden pt-16 md:pt-20">
      {/* Animated 3D DharmaR emblem — click to enter Releases, pulses with the live radio */}
      <Hero3DLogo onActivate={goToReleases} audioActive={isRadioActive} />

      {/* Tap-to-explore hint (first-time visitors) */}
      <div
        data-testid="hero-tap-hint"
        className={`absolute top-[60%] md:top-[62%] left-1/2 -translate-x-1/2 z-20 pointer-events-none transition-all duration-700 ${
          showHint ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <div
          className="hero-hint-bob flex items-center gap-2 px-4 py-1.5 rounded-full text-xs md:text-sm text-[#d7e6b0] bg-black/40 backdrop-blur-sm border border-[#bfff00]/25"
        >
          <MousePointerClick className="w-3.5 h-3.5 text-[#bfff00]" />
          Tap to explore
        </div>
      </div>


      {/* Tagline */}
      <div
        data-testid="hero-tagline"
        className={`absolute bottom-36 md:bottom-32 left-1/2 -translate-x-1/2 z-10 px-6 w-full max-w-3xl flex justify-center transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
        style={{ textShadow: '0 2px 18px rgba(0,0,0,0.9)' }}
      >
        <div
          ref={parallaxRef}
          className="transition-transform duration-300 ease-out will-change-transform"
        >
          <p className="group cursor-default text-center text-sm md:text-base leading-relaxed tracking-wide text-[#d7e6b0] font-light transition-all duration-500 ease-out hover:-translate-y-0.5 hover:tracking-wider hover:text-[#eaf6c8]">
            Dedicated to{' '}
            <em className="text-[#bfff00] not-italic font-medium transition-all duration-500 group-hover:[text-shadow:0_0_16px_rgba(191,255,0,0.85)]">the raw</em>,{' '}
            <em className="text-[#bfff00] not-italic font-medium transition-all duration-500 group-hover:[text-shadow:0_0_16px_rgba(191,255,0,0.85)]">the obscure</em>, and the{' '}
            <em className="text-[#bfff00] not-italic font-medium transition-all duration-500 group-hover:[text-shadow:0_0_16px_rgba(191,255,0,0.85)]">unapologetically psychedelic</em>
            <span className="block mt-1 text-[#9fb37e] transition-colors duration-500 group-hover:text-[#c7d9a0]">— exploring the underground evolution of Goa Trance.</span>
          </p>
        </div>
      </div>

      {/* Scroll indicator — rotating ring with gradient dot */}
      <div 
        className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="flex flex-col items-center gap-3">
          {/* Outer ring slowly spins, inner dot pulses */}
          <div className="relative w-10 h-10">
            <div 
              className="absolute inset-0 rounded-full border border-orange-500/40 animate-spin"
              style={{ animationDuration: '4s' }}
            />
            <div 
              className="absolute inset-0 rounded-full border border-transparent border-t-green-500/60 animate-spin"
              style={{ animationDuration: '6s', animationDirection: 'reverse' }}
            />
            {/* Center arrow dot */}
            <div className="absolute inset-0 flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="animate-bounce" style={{ animationDuration: '2s' }}>
                <path d="M7 2 L7 10 M3 7 L7 11 L11 7" stroke="url(#scrollGrad)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="scrollGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316"/>
                    <stop offset="100%" stopColor="#22c55e"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
          {/* Thin fading line */}
          <div className="w-px h-6 bg-gradient-to-b from-orange-500/50 to-transparent"></div>
        </div>
      </div>
    </section>
  );
};
